import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import CommonTable from "./CommonTable";
import CustomPagination from "../common/CustomPagination";
import Loader from "../common/loader";
import NoDataAvailable from "../common/NoDataAvailable";
import ColumnSelector from "./CommonSelector";
import { Box } from "@mui/material";
import { setDefaultFilterValues } from "../../redux/slice/filterSlice";
import { setFilters } from "../../redux/slice/columnFilter/index";
import { MultiSelect } from "../filters/MultiSelect";

const PaginatedDataTable = ({
    queryHook,
    title,
    tableProps,
    paginationEnabled,
    initialDateRange,
    appliedFilters,
    totalText,
    firstColumnWidth,
    isHeaderBold,
    isColumnSticky,
    headerFontSizes,
    isPopOver,
    stickyColumnsCount,
    filterElmDateRange,
    alignItems,
    showRowHighlighting,
    highlightRules,
    showEditColumn,
    showCheckboxColumn,
    customCellColor,
    customCellRender,
    defaultPageSize = 100,
    dialogType,
    showColumnSelector,
    toggleSwitchElm,
    showToggleSwitchElm,
    showTierSelector,
    tierChange,
    range,
    isClient,
    headerHeights,
    headerPadding,
    headerGap,
}) => {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(defaultPageSize);
    const [tierOptions, setTierOptions] = useState([]);
    const columnFilter = useSelector((state) => state.columnFilter);
    const dispatch = useDispatch();
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const selectedTier = appliedFilters?.tier || [];

    const { data, error, isFetching, refetch } = queryHook({
        appliedFilters,
        startDate: initialDateRange?.startDate,
        endDate: initialDateRange?.endDate,
        page: page,
        pageSize: rowsPerPage,
        ...(filterElmDateRange || {}),
    });

    const [visibleColumns, setVisibleColumns] = useState([]);
    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(1);
    };

    useEffect(() => {
        if (initialDateRange?.startDate && initialDateRange?.endDate) {
            setPage(1);
        }
    }, [initialDateRange]);

    useEffect(() => {
        setVisibleColumns(
            data?.columns
                ?.map((header) => header.key)
                ?.filter((key) => !columnFilter.includes(key)) // Exclude already visible keys
        );
        if (data?.defaultFilterValues) {
            dispatch(
                setDefaultFilterValues({
                    defaultFilterValues: data.defaultFilterValues,
                })
            );
        }

        if (showTierSelector && data?.tier_values?.length > 0) {
            const result = data?.tier_values.map((item) => {
                return {
                    id: item.id,
                    label: item.name,
                    color: item.color,
                    count: item.count,
                };
            });
            setTierOptions(result);
        }
    }, [data]);

    let staticContent;
    if (isFetching) {
        staticContent = <Loader isFullPage={true} isPopOver={isPopOver} />;
    } else if (error) {
        staticContent = "Error";
    } else if (
        !data ||
        !data.columns ||
        !data?.data?.length ||
        data?.data?.every((row) =>
            Object.keys(row).every(
                (key) =>
                    [
                        "username",
                        "email",
                        "id",
                        "role",
                        "ambit_sector",
                        "report_type",
                    ].includes(key) || row[key] === 0
            )
        )
    ) {
        staticContent = <NoDataAvailable />;
    }

    const handleColumnVisibilityChange = (selectedColumns) => {
        setVisibleColumns(selectedColumns);
        const allColumns = data?.columns?.map((header) => header.key);
        const missingColumns = allColumns.filter(
            (col) => !selectedColumns.includes(col)
        );
        dispatch(setFilters(missingColumns));
    };

    return staticContent ? (
        <div style={styles.staticContent}>{staticContent}</div>
    ) : (
        <div
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 0.5,
                        mb: 1,
                        mr: 0.3,
                    }}
                >
                    {showTierSelector && (
                        <MultiSelect
                            options={tierOptions}
                            defaultSelected={
                                selectedTier.length
                                    ? tierOptions.filter((opt) =>
                                          selectedTier.includes(opt.id)
                                      )
                                    : tierOptions
                            }
                            onChange={tierChange}
                            itemText="tier"
                            isTierSelector={false}
                        />
                    )}

                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1,
                            }}
                        >
                            <Box>
                                {showToggleSwitchElm && <>{toggleSwitchElm}</>}
                            </Box>
                            {showColumnSelector && (
                                <ColumnSelector
                                    headers={data?.columns}
                                    onColumnVisibilityChange={
                                        handleColumnVisibilityChange
                                    }
                                    defaultSelected={visibleColumns}
                                />
                            )}
                        </Box>
                    </Box>
                </Box>

                <CommonTable
                    title={title}
                    headers={data.columns}
                    data={data.data}
                    hasPagination={paginationEnabled}
                    {...tableProps}
                    totalText={totalText}
                    firstColumnWidth={firstColumnWidth}
                    isHeaderBold={isHeaderBold}
                    isColumnSticky={isColumnSticky}
                    headerFontSizes={headerFontSizes}
                    stickyColumnsCount={stickyColumnsCount}
                    alignItems={alignItems}
                    showRowHighlighting={showRowHighlighting}
                    highlightRules={highlightRules}
                    showEditColumn={showEditColumn}
                    showCheckboxColumn={showCheckboxColumn}
                    refetch={refetch}
                    customCellColor={customCellColor}
                    customCellRender={customCellRender}
                    dialogType={dialogType}
                    showColumnSelector={showColumnSelector}
                    visibleColumns={visibleColumns}
                    range={range}
                    isClient={isClient}
                    headerHeights={headerHeights}
                    headerPadding={headerPadding}
                    headerGap={headerGap}
                />
            </div>
            {data?.pagination && paginationEnabled && (
                <div
                    style={{
                        flexShrink: 0,
                        borderTop: "1px solid rgba(224, 224, 224, 1)",
                    }}
                >
                    <CustomPagination
                        count={data.pagination.total}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                    />
                </div>
            )}
        </div>
    );
};

const styles = {
    staticContent: {
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
};

PaginatedDataTable.propTypes = {
    appliedFilters: PropTypes.object,
    queryHook: PropTypes.func.isRequired,
    title: PropTypes.string,
    tableProps: PropTypes.object,
    paginationEnabled: PropTypes.bool,
    isPopOver: PropTypes.bool,
    showRowHighlighting: PropTypes.bool,
};

PaginatedDataTable.defaultProps = {
    appliedFilters: {},
    tableProps: {},
    paginationEnabled: true,
    isPopOver: false,
    showRowHighlighting: false,
};

export default PaginatedDataTable;
